import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, dateOnly, API_BASE } from "@/lib/api";
import { ArrowLeft, Download, Receipt, CreditCard, AlertTriangle, FileText } from "lucide-react";

const BUCKET_LABELS = {
    vigente: "Vigente",
    "1_30": "1-30 d",
    "31_60": "31-60 d",
    "61_90": "61-90 d",
    "90_plus": "90+ d",
};

const BUCKETS = ["vigente", "1_30", "31_60", "61_90", "90_plus"];

export default function ClientStatement() {
    const { client_id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/finance/statement/${client_id}`);
                setData(data);
            } catch (e) {
                console.warn(e);
            }
        })();
    }, [client_id]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando estado de cuenta...</div>;

    const { client, summary, invoices, payments } = data;
    const creditUsedPct = summary.credit_limit > 0
        ? Math.min(100, (summary.total_balance / summary.credit_limit) * 100)
        : 0;
    const overBuckets = BUCKETS.filter((b) => b !== "vigente").reduce((s, b) => s + (summary.buckets[b] || 0), 0);

    return (
        <div className="max-w-6xl space-y-4" data-testid="client-statement-page">
            <Link to="/aging" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Antigüedad de saldos
            </Link>

            {/* Header */}
            <div className="fm-card p-5 bg-slate-900 text-white flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">ESTADO DE CUENTA</div>
                    <h2 className="font-display font-black text-2xl tracking-tight">{client.business_name}</h2>
                    <div className="text-xs text-slate-300 mt-1 font-mono">
                        RFC: {client.rfc || "—"} · Tipo: {client.type} · Crédito: {summary.credit_days} días
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Saldo total</div>
                    <div className="font-mono font-black text-3xl">{money(summary.total_balance)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                        {summary.open_invoices} factura(s) abierta(s)
                    </div>
                    <a
                        href={`${API_BASE}/pdf/statement/${client_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid="download-statement-btn"
                        title="Descarga el estado de cuenta en PDF para enviar al cliente"
                    >
                        <Download className="w-3.5 h-3.5" /> Descargar PDF
                    </a>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="statement-kpis">
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total facturado</div>
                    <div className="font-mono font-bold text-xl">{money(summary.total_billed)}</div>
                </div>
                <div className="fm-card p-4 border-l-4 border-l-emerald-500">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cobrado</div>
                    <div className="font-mono font-bold text-xl text-emerald-700">{money(summary.total_paid)}</div>
                </div>
                <div className="fm-card p-4 border-l-4 border-l-amber-500">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Saldo pendiente</div>
                    <div className="font-mono font-bold text-xl">{money(summary.total_balance)}</div>
                </div>
                <div className="fm-card p-4 border-l-4 border-l-red-600">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vencido</div>
                    <div className="font-mono font-bold text-xl text-red-600">{money(overBuckets)}</div>
                </div>
            </div>

            {/* Credit usage */}
            {summary.credit_limit > 0 && (
                <div className="fm-card p-5" data-testid="credit-usage">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Uso de línea de crédito</div>
                            <div className="font-mono font-bold text-lg">
                                {money(summary.total_balance)} / {money(summary.credit_limit)}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Disponible</div>
                            <div className={`font-mono font-bold text-lg ${summary.credit_available > 0 ? "text-emerald-700" : "text-red-600"}`}>
                                {money(summary.credit_available)}
                            </div>
                        </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
                        <div
                            className={`h-full ${creditUsedPct > 90 ? "bg-red-600" : creditUsedPct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                            style={{ width: `${creditUsedPct}%` }}
                        />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 text-right font-mono">
                        {Math.round(creditUsedPct)}% utilizado
                    </div>
                </div>
            )}

            {/* Aging breakdown */}
            <div className="fm-card p-5">
                <h3 className="font-display font-bold text-base mb-3">Antigüedad del saldo</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {BUCKETS.map((b) => (
                        <div key={b} className="text-center p-3 border border-slate-200 rounded-sm" data-testid={`statement-bucket-${b}`}>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{BUCKET_LABELS[b]}</div>
                            <div className={`font-mono font-bold text-base mt-1 ${b === "90_plus" || b === "61_90" ? "text-red-600" : ""}`}>
                                {money(summary.buckets[b] || 0)}
                            </div>
                        </div>
                    ))}
                </div>
                {overBuckets > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border-l-4 border-l-red-600 flex items-start gap-2 text-xs" data-testid="statement-overdue-alert">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            Este cliente tiene <strong>{money(overBuckets)}</strong> en saldo vencido.
                            Recomendamos contacto de cobranza inmediato si supera 60 días.
                        </div>
                    </div>
                )}
            </div>

            {/* Invoices */}
            <div className="fm-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-display font-bold text-base flex items-center gap-2"><Receipt className="w-4 h-4" /> Facturas</h3>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                        {invoices.length} doc(s)
                    </div>
                </div>
                {invoices.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">Sin facturas emitidas.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="statement-invoices">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Folio", "Emisión", "Vencimiento", "Total", "Pagado", "Saldo", "Días", "Estatus", "PDF"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 font-mono text-xs font-bold">{inv.folio}</td>
                                    <td className="py-2 px-3 text-xs font-mono">{dateOnly(inv.issue_date)}</td>
                                    <td className="py-2 px-3 text-xs font-mono">{dateOnly(inv.due_date)}</td>
                                    <td className="py-2 px-3 text-right font-mono">{money(inv.total)}</td>
                                    <td className="py-2 px-3 text-right font-mono text-emerald-700">{money(inv.paid_amount)}</td>
                                    <td className="py-2 px-3 text-right font-mono font-bold">{money(inv.balance)}</td>
                                    <td className="py-2 px-3 text-center">
                                        {inv.days_overdue > 0
                                            ? <span className="fm-badge fm-badge-critical">{inv.days_overdue}d</span>
                                            : inv.status === "pagado"
                                                ? <span className="fm-badge fm-badge-positive">OK</span>
                                                : <span className="fm-badge fm-badge-neutral">Vigente</span>}
                                    </td>
                                    <td className="py-2 px-3">
                                        <span className={`fm-badge ${inv.status === "pagado" ? "fm-badge-positive" : inv.status === "pago_parcial" ? "fm-badge-warning" : "fm-badge-neutral"}`}>{inv.status}</span>
                                    </td>
                                    <td className="py-2 px-3">
                                        <a href={`${API_BASE}/pdf/invoice/${inv.id}`} target="_blank" rel="noreferrer" className="text-xs text-slate-700 hover:text-red-600 inline-flex items-center gap-1">
                                            <Download className="w-3.5 h-3.5" />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Payments */}
            <div className="fm-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-display font-bold text-base flex items-center gap-2"><CreditCard className="w-4 h-4" /> Pagos recibidos</h3>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                        {payments.length} pago(s)
                    </div>
                </div>
                {payments.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">Sin pagos registrados.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="statement-payments">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Fecha", "Monto", "Método", "Referencia", "Notas"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((p) => (
                                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 text-xs font-mono">{dateOnly(p.payment_date)}</td>
                                    <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">{money(p.amount)}</td>
                                    <td className="py-2 px-3 text-xs">{p.method}</td>
                                    <td className="py-2 px-3 text-xs font-mono">{p.reference || "—"}</td>
                                    <td className="py-2 px-3 text-xs text-slate-500 truncate max-w-xs">{p.notes || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 font-mono text-center py-4 flex items-center justify-center gap-2">
                <FileText className="w-3 h-3" /> Documento informativo · Generado el {data.today}
            </div>
        </div>
    );
}
